import React, { useState, useEffect } from "react";
import "./App.css";
import Attendance from "./Attendance";
import Profile from "./Profile";
import ClassAssignment from "./ClassAssignment";
import Students from "./Students";
import Allergies from "./Allergies";
import Users from "./Users";
import AttendanceDates from "./AttendanceDates";
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc } from "firebase/firestore";
import dayjs from "dayjs";
import { auth, provider, db } from "./firebase-config";
import {
  AppBar,
  Tabs,
  Tab,
  Box,
  Typography,
  Toolbar,
  Menu,
  MenuItem,
  IconButton,
  TextField,
  Button,
  Link,
  Container,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MuiCard from "@mui/material/Card";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import EmailIcon from "@mui/icons-material/Email";
import { GoogleIcon } from "./CustomIcons";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]); // Multiple roles
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState(""); // For capturing display name
  const [isRegistering, setIsRegistering] = useState(false); // For toggling Profile
  const [tabIndex, setTabIndex] = useState(""); // Active tab index
  const [anchorEl, setAnchorEl] = useState(null); // For menu
  const [drawerOpen, setDrawerOpen] = useState(false); // For drawer
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);

        // Fetch roles from Firestore
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists()) {
          setRoles(userDoc.data().roles || []); // Assuming roles is an array
        } else {
          // Default roles for new users
          const defaultRoles = ["parent"];
          await setDoc(doc(db, "users", currentUser.uid), {
            roles: defaultRoles,
            displayName: currentUser.displayName || "No Name",
            email: currentUser.email,
          });
          setRoles(defaultRoles);
        }

        // --- Blue Card Expiry Check ---
        try {
          const settingsRef = doc(db, "system", "reminders");
          const settingsSnap = await getDoc(settingsRef);
          const today = dayjs().format("YYYY-MM-DD");

          let lastChecked = null;
          if (settingsSnap.exists()) {
            lastChecked = settingsSnap.data().lastBlueCardCheck;
          }

          if (lastChecked !== today) {
            // Optimistically update the date to prevent race conditions on simultaneous logins
            await setDoc(settingsRef, { lastBlueCardCheck: today }, { merge: true });

            const usersRef = collection(db, "users");
            const q = query(usersRef, where("roles", "array-contains-any", ["teacher", "volunteer", "first-aid"]));
            const querySnapshot = await getDocs(q);
            const oneMonthFromNow = dayjs().add(1, 'month');
            const oneWeekFromNow = dayjs().add(1, 'week');

            const updates = [];

            querySnapshot.forEach((docSnapshot) => {
              const userData = docSnapshot.data();
              if (!userData.blueCardExpiry) return;

              const expiryDate = dayjs(userData.blueCardExpiry);
              if (!expiryDate.isValid() || !expiryDate.isAfter(dayjs())) return;

              if (expiryDate.isBefore(oneWeekFromNow)) {
                // Second reminder: within 1 week of expiry
                if (!userData.blueCardSecondReminderSent) {
                  updates.push(async () => {
                    const mailDocRef = doc(collection(db, "mail"));
                    await setDoc(mailDocRef, {
                      to: userData.email,
                      message: {
                        subject: "Urgent Action Required: Blue Card Expiring Soon",
                        html: `Dear ${userData.displayName || 'User'},<br><br>
                               This is a reminder that your Blue Card is set to expire in less than a week on <b>${expiryDate.format("DD-MM-YYYY")}</b>.<br>
                               Please renew it immediately at <a href="https://my.bluecard.qld.gov.au/">https://my.bluecard.qld.gov.au/</a> and update your profile with new Blue Card details.<br><br>
                               Kind Regards,<br>Catechism Dept.<br>St Marys Syro Malabar Mission, Sunshine Coast`
                      }
                    });
                    await updateDoc(docSnapshot.ref, {
                      blueCardReminderSent: true,
                      blueCardSecondReminderSent: true
                    });
                  });
                }
              } else if (expiryDate.isBefore(oneMonthFromNow)) {
                // First reminder: within 1 month of expiry
                if (!userData.blueCardReminderSent) {
                  updates.push(async () => {
                    const mailDocRef = doc(collection(db, "mail"));
                    await setDoc(mailDocRef, {
                      to: userData.email,
                      message: {
                        subject: "Action Required: Blue Card Expiring Soon",
                        html: `Dear ${userData.displayName || 'User'},<br><br>
                               This is a reminder that your Blue Card is set to expire on <b>${expiryDate.format("DD-MM-YYYY")}</b>.<br>
                               Please renew it at <a href="https://my.bluecard.qld.gov.au/">https://my.bluecard.qld.gov.au/</a> and update your profile with new Blue Card details.<br><br>
                               Kind Regards,<br>Catechism Dept.<br>St Marys Syro Malabar Mission, Sunshine Coast`
                      }
                    });
                    await updateDoc(docSnapshot.ref, {
                      blueCardReminderSent: true
                    });
                  });
                }
              }
            });

            // Execute all writes concurrently
            await Promise.all(updates.map(fn => fn()));
          }
        } catch (error) {
          console.error("Error checking Blue Card expiries:", error);
        }
        // --- End Check ---
      } else {
        setUser(null);
        setRoles([]);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (roles && roles.length > 0 && !tabIndex) {
      if (roles.includes("teacher") || roles.includes("principal")) {
        setTabIndex("attendance");
      } else if (roles.includes("first-aid")) {
        setTabIndex("allergies");
      } else if (roles.includes("director")) {
        setTabIndex("students");
      } else if (roles.includes("parent")) {
        setTabIndex("profile");
      } else if (roles.includes("admin")) {
        setTabIndex("classes");
      }
    }
  }, [roles, tabIndex]);

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Save user data to Firestore
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (!userDoc.exists()) {
        await setDoc(doc(db, "users", user.uid), {
          roles: ["parent"], // Default role
          displayName: user.displayName || "No Name",
          email: user.email,
          photoURL: user.photoURL || "", // Save photoURL
        });
      }
      setEmail(user.email);
      console.log("Google Login successful!");
      window.location.reload(); // Refresh the page after successful login
    } catch (error) {
      console.error("Google Login failed: ", error);
    }
  };

  const handleEmailLogin = async () => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      window.location.reload(); // Refresh the page after successful login
    } catch (error) {
      console.error("Email Login failed: ", error);
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleEmailRegister = async () => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const newUser = userCredential.user;

      // Update the displayName in Firebase Authentication
      await updateProfile(newUser, {
        displayName: displayName || "No Name",
      });

      // Add displayName and default roles to Firestore
      await setDoc(doc(db, "users", newUser.uid), {
        roles: ["parent"],
        displayName: displayName || "No Name", // Save displayName
        email: email, // Save email
        photoURL: newUser.photoURL || "", // Save photoURL
      });

      setSnackbarMessage("Profile successful. Please log in.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsRegistering(false);
    } catch (error) {
      console.error("Profile failed: ", error);
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const isEmailValid = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isLoginDisabled = !isEmailValid(email) || !password;
  const isRegisterDisabled = !displayName || !isEmailValid(email) || !password;

  const handlePasswordReset = async () => {
    try {
      await sendPasswordResetEmail(auth, email);
      setSnackbarMessage("Password reset email sent!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Password reset failed: ", error);
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setRoles([]);
      setTabIndex("");
    } catch (error) {
      console.error("Logout failed: ", error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const drawer = (
    <div
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List id="drawer-list">
        {(roles.includes("principal") || roles.includes("teacher")) && (
          <ListItem button onClick={() => setTabIndex("attendance")}>
            <ListItemText primary="Attendance" />
          </ListItem>
        )}
        {(roles.includes("principal") ||
          roles.includes("teacher") ||
          roles.includes("first-aid")) && (
          <ListItem button onClick={() => setTabIndex("allergies")}>
            <ListItemText primary="Allergies" />
          </ListItem>
        )}
        <ListItem
          button
          onClick={() => setTabIndex("profile")}
        >
          <ListItemText primary="Profile" />
        </ListItem>
        {(roles.includes("principal") || roles.includes("admin")) && (
          <ListItem button onClick={() => setTabIndex("classes")}>
            <ListItemText primary="Classes" />
          </ListItem>
        )}
        {(roles.includes("principal") ||
          roles.includes("admin") ||
          roles.includes("director")) && (
          <ListItem button onClick={() => setTabIndex("students")}>
            <ListItemText primary="Students" />
          </ListItem>
        )}
        {(roles.includes("principal") ||
          roles.includes("admin") ||
          roles.includes("director")) && (
          <ListItem
            button
            onClick={() => setTabIndex("users")}
          >
            <ListItemText primary="Users" />
          </ListItem>
        )}
        {roles.includes("admin") && (
          <ListItem
            button
            onClick={() => setTabIndex("dates")}
          >
            <ListItemText primary="Dates" />
          </ListItem>
        )}
      </List>
    </div>
  );

  if (!user) {
    return (
      <Container className="App">
        <div>
          <Card variant="outlined">
            <Box
              noValidate
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                padding: 2,
              }}
            >
              <Avatar src="/icons/favicon.png" alt="Logo" />
              {isRegistering ? (
                <>
                  <Typography>Register</Typography>
                  <TextField
                    id="register-name-textfield"
                    label="Name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    id="register-email-textfield"
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    id="register-password-textfield"
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    margin="normal"
                  />
                  <Button
                    id="register-button"
                    variant="contained"
                    color="primary"
                    onClick={handleEmailRegister}
                    disabled={isRegisterDisabled}
                  >
                    Register
                  </Button>
                  <Typography>
                    Already have an account?{" "}
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setIsRegistering(false)}
                    >
                      Log in
                    </Link>
                  </Typography>
                </>
              ) : (
                <>
                  <Typography>Sign In</Typography>
                  <TextField
                    id="login-email-textfield"
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    id="login-password-textfield"
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    margin="normal"
                  />
                  <Button
                    id="login-button"
                    variant="contained"
                    color="secondary"
                    onClick={handleEmailLogin}
                    disabled={isLoginDisabled}
                    startIcon={<EmailIcon />}
                  >
                    Login
                  </Button>
                  <Typography>
                    Don’t have an account?{" "}
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setIsRegistering(true)}
                    >
                      Register
                    </Link>
                  </Typography>
                  <Typography>
                    Forgot your password?{" "}
                    {isEmailValid(email) ? (
                      <Link
                        component="button"
                        variant="body2"
                        onClick={handlePasswordReset}
                      >
                        Reset Password
                      </Link>
                    ) : (
                      <b>Enter e-mail</b>
                    )}
                  </Typography>
                </>
              )}
            </Box>
            <Divider>or</Divider>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                padding: 2,
              }}
            >
              <Button
                id="google-login-button"
                variant="outlined"
                color="primary"
                onClick={handleGoogleLogin}
                startIcon={<GoogleIcon />}
              >
                Login with Google
              </Button>
            </Box>
          </Card>
        </div>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    );
  }

  return (
    <div className="App">
      <AppBar position="static" style={{ backgroundColor: "orange" }}>
        <Toolbar>
          <IconButton
            id="drawer-button"
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
          >
            <MenuIcon />
          </IconButton>
          <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
            {drawer}
          </Drawer>
          <Avatar
            src="/icons/favicon.png"
            alt="Logo"
            style={{ marginRight: 10 }}
          />
          <Typography variant="h6" style={{ flexGrow: 1 }}>
            Catechism
          </Typography>
          {isMobile ? null : (
            <Tabs value={tabIndex} onChange={handleTabChange}>
              {(roles.includes("teacher") || roles.includes("principal")) && (
                <Tab id="tab-attendance" value="attendance" label="Attendance" />
              )}
              {(roles.includes("teacher") ||
                roles.includes("principal") ||
                roles.includes("first-aid")) && (
                <Tab id="tab-allergies" value="allergies" label="Allergies" />
              )}
              {roles.includes("parent") && (
                <Tab id="tab-profile" value="profile" label="Profile" />
              )}
              {(roles.includes("principal") || roles.includes("admin")) && (
                <Tab id="tab-classes" value="classes" label="Classes" />
              )}
              {(roles.includes("principal") ||
                roles.includes("admin") ||
                roles.includes("director")) && (
                <Tab id="tab-students" value="students" label="Students" />
              )}
              {(roles.includes("principal") ||
                roles.includes("admin") ||
                roles.includes("director")) && (
                <Tab id="tab-users" value="users" label="Users" />
              )}
              {roles.includes("admin") && (
                <Tab id="tab-dates" value="dates" label="Dates" />
              )}
            </Tabs>
          )}
          <div>
            <IconButton
              edge="end"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              {user.photoURL ? (
                <Avatar src={user.photoURL} alt="Profile Picture" />
              ) : (
                <AccountCircleIcon />
              )}
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled>{user.displayName || user.email}</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>

      {(roles.includes("teacher") || roles.includes("principal")) && (
        <TabPanel value={tabIndex} index="attendance">
          <Attendance
            isPrincipal={roles.includes("principal")}
            displayName={user.displayName}
            userEmail={user.email}
            userId={user.uid}
          />
        </TabPanel>
      )}

      {(roles.includes("teacher") ||
        roles.includes("principal") ||
        roles.includes("first-aid")) && (
        <TabPanel value={tabIndex} index="allergies">
          <Allergies />
        </TabPanel>
      )}

      <TabPanel
        value={tabIndex}
        index="profile"
      >
        <Profile parentId={user.uid} />
      </TabPanel>

      {(roles.includes("principal") || roles.includes("admin")) && (
        <TabPanel value={tabIndex} index="classes">
          <ClassAssignment />
        </TabPanel>
      )}
      {(roles.includes("principal") ||
        roles.includes("admin") ||
        roles.includes("director")) && (
        <TabPanel value={tabIndex} index="students">
          <Students isAdmin={roles.includes("admin")} userRoles={roles} />
        </TabPanel>
      )}
      {(roles.includes("principal") ||
        roles.includes("admin") ||
        roles.includes("director")) && (
        <TabPanel
          value={tabIndex}
          index="users"
        >
          <Users isAdmin={roles.includes("admin")} userRoles={roles} />
        </TabPanel>
      )}
      {roles.includes("admin") && (
        <TabPanel
          value={tabIndex}
          index="dates"
        >
          <AttendanceDates />
        </TabPanel>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default App;
